import { useCallback } from "react";
import useAjax from "@apis/useAjax";
import { useSnackbar } from "notistack";
import useShowPDF from "@util/hook/useShowPDF";
import {
	GET_DOC_HEALTH_DECLARATION_OPTION_API,
	GET_DOC_HEALTH_DECLARATION_HISTORY_API,
	GET_DOC_HEALTH_DECLARATION_CONTRACTID_API,
	POST_DOC_HEALTH_DECLARATION_CONTRACTID_API,
	DELETE_DOC_HEALTH_DECLARATION_CONTRACTID_API,
	GET_DOC_HEALTH_DECLARATION_TEMPORARY_API,
	POST_DOC_HEALTH_DECLARATION_TEMPORARY_API,
	DELETE_DOC_HEALTH_DECLARATION_TEMPORARY_API,
	GET_DOC_HEALTH_DECLARATION_VALID_API,
	GET_DOC_HEALTH_DECLARATION_VALID_TODAY_API,
} from "@apis/apiPath";

const useDocHealthDeclarationApi = () => {
	const Ajax = useAjax();
	const showPDF = useShowPDF();
	const { enqueueSnackbar } = useSnackbar();

	// 健康申告書歷史頁searchbar選項
	const getInitHealthDeclarationOptionApi = useCallback(
		async () => {
			const resp = await Ajax.get(GET_DOC_HEALTH_DECLARATION_OPTION_API);
			if (!resp) enqueueSnackbar("讀取資料失敗,請重新整理", { variant: "error" });
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 健康申告書查詢記錄
	const getDocHealthDeclarationHistoryApi = useCallback(
		async params => {
			const resp = await Ajax.get(GET_DOC_HEALTH_DECLARATION_HISTORY_API, { ...params });
			if (!resp) enqueueSnackbar("查詢失敗,請重新再操作一次", { variant: "error" });
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 健康申告書初始資料
	const getDocHealthDeclarationContractApi = useCallback(
		async (params = {}, contractID) => {
			const resp = await Ajax.get(GET_DOC_HEALTH_DECLARATION_CONTRACTID_API(contractID), params);
			if (!resp) enqueueSnackbar("讀取資料失敗,請重新整理", { variant: "error" });
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 新增健康申告書合約
	const postDocHealthDeclarationContractApi = useCallback(
		async (params = {}, contractID) =>
			await Ajax.post(POST_DOC_HEALTH_DECLARATION_CONTRACTID_API(contractID), {
				...params,
			}),
		// eslint-disable-next-line
		[]
	);

	// 刪除健康申告書合約
	const deleteDocHealthDeclarationContractApi = useCallback(
		async contractID => {
			let resp = await Ajax.delete(DELETE_DOC_HEALTH_DECLARATION_CONTRACTID_API(contractID));
			if (resp) {
				enqueueSnackbar("刪除成功", { variant: "success" });
			} else {
				enqueueSnackbar("刪除失敗,請再操作一次", { variant: "error" });
			}
		},
		// eslint-disable-next-line
		[]
	);

	// 確認是否有暫存的健康申告書
	const getDocHealthDeclarationTemporaryApi = useCallback(
		async params => {
			const resp = await Ajax.get(GET_DOC_HEALTH_DECLARATION_TEMPORARY_API, params);
			// TODO: Allen
			if (resp !== 0 && !resp) {
				enqueueSnackbar("讀取資料失敗,請重新整理", { variant: "error" });
			}
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 儲存暫存健康申告書
	const postDocHealthDeclarationTemporaryApi = useCallback(
		async params => {
			let resp = await Ajax.post(POST_DOC_HEALTH_DECLARATION_TEMPORARY_API, params);
			if (resp) {
				enqueueSnackbar("暫存健康申告書成功", { variant: "success" });
			} else {
				enqueueSnackbar("新增暫存失敗，請重新操作", { variant: "error" });
				resp = null;
			}
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 刪除暫存健康申告書
	const deleteDocHealthDeclarationTemporaryApi = useCallback(
		async params => {
			const resp = await Ajax.delete(DELETE_DOC_HEALTH_DECLARATION_TEMPORARY_API, params);
			if (!resp) enqueueSnackbar("刪除失敗，請重新操作", { variant: "error" });
			return resp;
		},
		// eslint-disable-next-line
		[]
	);

	// 健康申告書下載PDF
	const getDocHealthDeclarationPDFApi = useCallback(
		async params => {
			const { contractUrl } = params;
			let url = contractUrl;
			showPDF({
				open: true,
				pdfUrl: url,
			});
		},
		// eslint-disable-next-line
		[]
	);

	// 檢查對象的健康申告書是否有效
	const getDocHealthDeclarationIsValidApi = useCallback(
		async params => await Ajax.get(GET_DOC_HEALTH_DECLARATION_VALID_API, params),
		// eslint-disable-next-line
		[]
	);

	// 檢查是否需要填寫健康申告書(此條路是特殊判斷，檢查是否是舊版本或是超過30天未填寫)
	const getHealthDeclarationRequiredApi = useCallback(
		async params => await Ajax.get(GET_DOC_HEALTH_DECLARATION_VALID_TODAY_API, params),
		// eslint-disable-next-line
		[]
	);

	return {
		getInitHealthDeclarationOptionApi,
		getDocHealthDeclarationHistoryApi,
		getDocHealthDeclarationContractApi,
		postDocHealthDeclarationContractApi,
		deleteDocHealthDeclarationContractApi,
		getDocHealthDeclarationTemporaryApi,
		postDocHealthDeclarationTemporaryApi,
		deleteDocHealthDeclarationTemporaryApi,
		getDocHealthDeclarationPDFApi,
		getDocHealthDeclarationIsValidApi,
		getHealthDeclarationRequiredApi,
	};
};

export default useDocHealthDeclarationApi;
